/**
 * Floating popup anchored above the task-list button. Shows all non-dismissed
 * tasks and closes on outside click. Clearing unread completions belongs to the
 * open action itself, so `ChatHistory` does it in the toggle handler.
 */
import React from "react";
import ReactDOM from "react-dom";
import { FiX } from "react-icons/fi";
import { shouldStopPolling } from "types/TaskRegistry";
import type { Task } from "types/Task";
import useTasksQuery, {
  TASKS_QUERY_KEY,
} from "services/dbApi/hooks/useTasksQuery";
import usePatchTaskMutation from "services/dbApi/hooks/usePatchTaskMutation";
import { useQueryClient } from "@tanstack/react-query";
import TaskListEntry from "./TaskListEntry";
import styles from "./TaskList.module.scss";

interface TaskListPopupProps {
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}

export default function TaskListPopup({
  buttonRef,
  onClose,
}: TaskListPopupProps) {
  const tasksQuery = useTasksQuery();
  const patchMutation = usePatchTaskMutation();
  const queryClient = useQueryClient();
  const popupRef = React.useRef<HTMLDivElement>(null);

  const tasks = tasksQuery.data ?? [];

  React.useEffect(() => {
    function handleClick(event: MouseEvent) {
      const target = event.target as Node;
      if (
        popupRef.current &&
        !popupRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        onClose();
      }
    }
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [onClose, buttonRef]);

  const [position, setPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  React.useEffect(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPosition({
      top: rect.top - 8,
      left: rect.left,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClearTerminal = () => {
    const terminal = tasks.filter(
      (j) => shouldStopPolling(j.status) && !j.dismissed
    );
    if (terminal.length === 0) return;
    // Optimistic cache update (one pass) then a PATCH per row.
    const terminalIds = new Set(terminal.map((j) => j.id));
    queryClient.setQueryData<Task[]>(TASKS_QUERY_KEY, (prev) =>
      prev?.map((j) => (terminalIds.has(j.id) ? { ...j, dismissed: true } : j))
    );
    for (const task of terminal) {
      patchMutation.mutate({ id: task.id, patch: { dismissed: true } });
    }
  };

  const handleRemoveTask = (jobId: string) => {
    const task = tasks.find((j) => j.jobId === jobId);
    if (!task) return;
    queryClient.setQueryData<Task[]>(TASKS_QUERY_KEY, (prev) =>
      prev?.map((j) => (j.id === task.id ? { ...j, dismissed: true } : j))
    );
    patchMutation.mutate({ id: task.id, patch: { dismissed: true } });
  };

  const entries = tasks
    .filter((j) => !j.dismissed)
    .sort((a, b) => {
      const aActive = !shouldStopPolling(a.status);
      const bActive = !shouldStopPolling(b.status);
      if (aActive !== bActive) return aActive ? -1 : 1;
      return b.createdAt.localeCompare(a.createdAt);
    });

  if (position === null) return null;

  const popup = (
    <div
      ref={popupRef}
      className={styles.popup}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: "translateY(-100%)",
      }}
    >
      <div className={styles.header}>
        <h3>Tasks</h3>
        <span className={styles.headerActions}>
          {entries.some((e) => shouldStopPolling(e.status)) && (
            <button
              className={styles.clearButton}
              onClick={handleClearTerminal}
            >
              Clear completed
            </button>
          )}
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close task list"
          >
            <FiX />
          </button>
        </span>
      </div>
      <div className={styles.body}>
        {entries.length === 0 ? (
          <div className={styles.emptyState}>
            No tasks yet. Tasks will appear here when tools start long-running
            operations.
          </div>
        ) : (
          entries.map((task) => (
            <TaskListEntry
              key={task.jobId}
              task={task}
              onNavigate={onClose}
              onRemove={handleRemoveTask}
            />
          ))
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(popup, document.body);
}
